import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import infoIcon from "../../assets/dashboard/info-icon.svg";
import calenderIcon from "../../assets/dashboard/calender-icon.svg";
import calenderDropdownIcon from "../../assets/dashboard/calender-dropdown-icon.svg";

const DateFilter = ({
  onDateRangeChange,
  className = "",
  isAgentDashboard = false,
}) => {
  const { t } = useTranslation();
  const initialRange = "today";
  const initialStartDate = moment();
  const initialEndDate = moment();
  const initialDisplayDate = moment().format("DD MMM YYYY");
  const initialLeftMonth = moment().startOf("month");
  const initialRightMonth = moment().add(1, "month").startOf("month");

  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(initialRange);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [displayDate, setDisplayDate] = useState(initialDisplayDate);
  const dropdownRef = useRef(null);

  const [leftMonth, setLeftMonth] = useState(initialLeftMonth);
  const [rightMonth, setRightMonth] = useState(initialRightMonth);
  const [isSelectingStart, setIsSelectingStart] = useState(true);

  const dateRangeOptions = [
    { value: "today", label: t("dashboard.dateRange.today") },
    { value: "last7", label: t("dashboard.dateRange.last7") },
    { value: "last15", label: t("dashboard.dateRange.last15") },
    { value: "last30", label: t("dashboard.dateRange.last30") },
    { value: "last90", label: t("dashboard.dateRange.last90") },
    { value: "thisYear", label: t("dashboard.dateRange.thisYear") },
    { value: "custom", label: t("dashboard.dateRange.custom") },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    updateDisplayDate();
  }, [startDate, endDate, selectedRange, onDateRangeChange]);

  const handleUpdate = () => {
    if (onDateRangeChange) {
      onDateRangeChange(startDate, endDate);
    }
    setSelectedRange(initialRange);
    setStartDate(moment(initialStartDate));
    setEndDate(moment(initialEndDate));
    setDisplayDate(initialDisplayDate);
    setLeftMonth(moment(initialLeftMonth));
    setRightMonth(moment(initialRightMonth));
    setIsOpen(false);
  };

  const updateDisplayDate = () => {
    if (selectedRange === "custom") {
      if (startDate.isSame(endDate, "day")) {
        setDisplayDate(startDate.format("DD MMM YYYY"));
      } else {
        setDisplayDate(
          `${startDate.format("DD MMM")} - ${endDate.format("DD MMM YYYY")}`
        );
      }
    } else {
      setDisplayDate(endDate.format("DD MMM YYYY"));
    }
  };

  const handleRangeSelect = (range) => {
    setSelectedRange(range);
    let newStartDate, newEndDate;

    switch (range) {
      case "today":
        newStartDate = moment();
        newEndDate = moment();
        break;
      case "last7":
        newStartDate = moment().subtract(6, "days");
        newEndDate = moment();
        break;
      case "last15":
        newStartDate = moment().subtract(14, "days");
        newEndDate = moment();
        break;
      case "last30":
        newStartDate = moment().subtract(29, "days");
        newEndDate = moment();
        break;
      case "last90":
        newStartDate = moment().subtract(89, "days");
        newEndDate = moment();
        break;
      case "thisYear":
        newStartDate = moment().startOf("year");
        newEndDate = moment();
        break;
      case "custom":
        newStartDate = startDate;
        newEndDate = endDate;
        break;
      default:
        newStartDate = startDate;
        newEndDate = endDate;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);

    setLeftMonth(newStartDate.clone().startOf("month"));
    setRightMonth(newStartDate.clone().add(1, "month").startOf("month"));

    if (range !== "custom" && onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
  };

  const isDisabledDate = (date) => {
    if (selectedRange === "today") {
      return date.isAfter(moment(), "day");
    }
    return false;
  };

  const handleDateClick = (date) => {
    if (selectedRange !== "custom") return;

    if (isSelectingStart) {
      setStartDate(date);
      setEndDate(date);
      setIsSelectingStart(false);
    } else {
      if (date.isBefore(startDate)) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
      setIsSelectingStart(true);
    }
  };

  const generateCalendarDays = (date) => {
    const start = date.clone().startOf("month").startOf("week");
    const end = date.clone().endOf("month").endOf("week");
    const days = [];
    let day = start.clone();

    while (day.isSameOrBefore(end)) {
      days.push(day.clone());
      day.add(1, "day");
    }

    return days;
  };

  const isInRange = (date) => {
    return (
      date.isSameOrAfter(startDate, "day") &&
      date.isSameOrBefore(endDate, "day")
    );
  };

  const isStartDate = (date) => date.isSame(startDate, "day");
  const isEndDate = (date) => date.isSame(endDate, "day");

  const handleCancel = () => {
    setSelectedRange(initialRange);
    setStartDate(moment(initialStartDate));
    setEndDate(moment(initialEndDate));
    setDisplayDate(initialDisplayDate);
    setLeftMonth(moment(initialLeftMonth));
    setRightMonth(moment(initialRightMonth));

    setIsOpen(false);
  };

  return (
    <div className={`relative z-30 ${className}`} ref={dropdownRef}>
      <div className="flex items-center gap-2">
        <div className="relative flex justify-between">
          <input
            type="text"
            value={displayDate}
            onClick={() => setIsOpen(!isOpen)}
            readOnly
            className={`w-[200px] bg-secondaryBg md:w-auto h-10 pl-10 pr-8 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:border-transparent text-center
              ${
                isAgentDashboard
                  ? "border-secondary text-secondary"
                  : "border-primary text-black"
              }`}
            placeholder="Select date"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <img
              src={calenderIcon}
              alt="calenderIcon"
              className={`w-4 h-4 relative ${
                isAgentDashboard ? "filter-blue" : ""
              }`}
            />
          </div>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <img
              src={calenderDropdownIcon}
              alt="calenderDropdownIcon"
              className={`w-2 h-2 relative ${
                isAgentDashboard ? "filter-blue" : ""
              }`}
            />
          </div>
        </div>
        <div className="group">
          <img
            src={infoIcon}
            alt="info"
            className={`w-4 h-4 cursor-pointer relative ${
              isAgentDashboard ? "filter-blue" : ""
            }`}
          />
          <div className="absolute left-60 top-1/2 -translate-y-14 w-[150px] px-2 py-1 bg-white text-primary text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
            {t("dashboard.tooltip.date")}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50 w-[720px]">
          <div className="flex">
            {/* Left Panel - Date Range Options */}
            <div className="w-1/4 p-4 border-r border-gray-200">
              <div className="space-y-3">
                {dateRangeOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="dateRange"
                      value={option.value}
                      checked={selectedRange === option.value}
                      onChange={() => handleRangeSelect(option.value)}
                      className="mr-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-primary">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Right Panel - Calendar Grids */}
            <div className="w-2/3 p-4">
              <div className="flex gap-4">
                {/* Left Calendar */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      onClick={() =>
                        setLeftMonth(leftMonth.clone().subtract(1, "month"))
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <div className="text-sm font-medium">
                      {leftMonth.format("MMM YYYY")}
                    </div>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      onClick={() =>
                        setLeftMonth(leftMonth.clone().add(1, "month"))
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-xs">
                    {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
                      <div
                        key={day}
                        className="p-2 text-center text-gray-500 font-medium"
                      >
                        {day}
                      </div>
                    ))}
                    {generateCalendarDays(leftMonth).map((date, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          !isDisabledDate(date) && handleDateClick(date)
                        }
                        disabled={isDisabledDate(date)}
                        className={`p-2 text-center text-sm rounded
                          ${
                            date.month() !== leftMonth.month()
                              ? "text-gray-300"
                              : ""
                          }
                          ${
                            isDisabledDate(date)
                              ? "text-gray-300 cursor-not-allowed"
                              : "hover:bg-blue-50"
                          }
                          ${
                            isStartDate(date)
                              ? "bg-blue-600 text-white rounded-l-full"
                              : ""
                          }
                          ${
                            isEndDate(date)
                              ? "bg-blue-700 text-white rounded-r-full"
                              : ""
                          }
                          ${
                            isInRange(date) &&
                            !isStartDate(date) &&
                            !isEndDate(date)
                              ? "bg-blue-100"
                              : ""
                          }
                        `}
                      >
                        {date.date()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Right Calendar */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      onClick={() =>
                        setRightMonth(rightMonth.clone().subtract(1, "month"))
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <div className="text-sm font-medium">
                      {rightMonth.format("MMM YYYY")}
                    </div>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      onClick={() =>
                        setRightMonth(rightMonth.clone().add(1, "month"))
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-xs">
                    {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
                      <div
                        key={day}
                        className="p-2 text-center text-gray-500 font-medium"
                      >
                        {day}
                      </div>
                    ))}
                    {generateCalendarDays(rightMonth).map((date, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          !isDisabledDate(date) && handleDateClick(date)
                        }
                        disabled={isDisabledDate(date)}
                        className={`p-2 text-center text-sm rounded
                          ${
                            date.month() !== rightMonth.month()
                              ? "text-gray-300"
                              : ""
                          }
                          ${
                            isDisabledDate(date)
                              ? "text-gray-300 cursor-not-allowed"
                              : "hover:bg-blue-50"
                          }
                          ${
                            isStartDate(date)
                              ? "bg-blue-600 text-white rounded-l-full"
                              : ""
                          }
                          ${
                            isEndDate(date)
                              ? "bg-blue-700 text-white rounded-r-full"
                              : ""
                          }
                          ${
                            isInRange(date) &&
                            !isStartDate(date) &&
                            !isEndDate(date)
                              ? "bg-blue-100"
                              : ""
                          }
                        `}
                      >
                        {date.date()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-5 p-4">
                <button
                  onClick={handleCancel}
                  className="px-5 py-1 text-sm font-bold text-secondary hover:text-white bg-white border border-secondary rounded-md hover:bg-[linear-gradient(-80deg,#136BF0_0%,#0C49A6_100%)]"
                >
                  {t("dashboard.dateRange.cancel")}
                </button>
                <button
                  onClick={handleUpdate}
                  className="px-5 py-1 text-sm font-bold text-white bg-primary rounded-md hover:bg-[linear-gradient(-80deg,#136BF0_0%,#0C49A6_100%)]"
                >
                  {t("dashboard.dateRange.update")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilter;
